import {
  IWorksheetComponent,
  WorksheetEquipmentDropdown,
  WorksheetGridFieldSection,
  WorksheetRecordGrid,
} from 'Components/Worksheets/Core';
import { getMessagesForMultiplePaths } from 'Components/Worksheets/Core/Helpers/messageHelpers';
import { hasNonEmptyValue, hasValue, valueOrDefault } from 'Lib/Any';
import { DateTimeDataFormat } from 'Lib/Format';
import { Guid } from 'Lib/Guid';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { QestlabListDropdown } from '../../../../../Components/List';
import ListType from '../../../../../Components/List/QestlabListDropdown/ListType';
import { actions as hydActions } from '../../../../../Data/ReduxViewModels/PsdHydrometer';
import {
  Button,
  Checkbox,
  DateInput,
  Field,
  NumericInput,
  RadioGroup,
  Spinner,
  TextOutput,
  TimeInput,
} from '../../../../Core';
import { NamePrefixModifier, withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { FieldSet, ListFieldSection, PageSection, SectionGroup } from '../../../../Layout';
import { IHydrometerReadingProps, ViscosityOfWaterDetermination } from '../../types';

let HydrometerMeasurementsPageSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const dispatch = useDispatch();
  const busyCreatingReadings = useSelector((s) => s.psdHydrometer.hydrometerWorksheetReadingManagement.status.creating);
  const hydrometerMeniscusCorrection = useSelector(
    createWorksheetSelector!('HydrometerPortion.Measurements.HydrometerCalibrationMeniscusCorrection'),
    deepEquals
  );
  const hydrometerSlope = useSelector(
    createWorksheetSelector!('HydrometerPortion.Measurements.HydrometerCalibrationSlope')
  );
  const hydrometerIntercept = useSelector(
    createWorksheetSelector!('HydrometerPortion.Measurements.HydrometerCalibrationIntercept')
  );
  const hydrometerCalibrationMismatch = useSelector(
    createWorksheetSelector!(`HydrometerPortion.Measurements.Equipment.HydrometerCalibrationMismatch`)
  ) as boolean;
  const hydrometerId = useSelector(
    createWorksheetSelector!('HydrometerPortion.Measurements.Equipment.Hydrometer.Id')
  ) as Guid;

  const hasImportedSieveAnalysis = useSelector(
    pipe(createWorksheetSelector!(`Preparation.AssociatedSieveAnalysisTest.ImportedTest.Id`), hasNonEmptyValue)
  ) as boolean;

  const readingRecordProps: IHydrometerReadingProps[] = useSelector(
    pipe(
      createWorksheetSelector!('HydrometerPortion.Measurements.Readings'),
      valueOrDefault([] as Array<any>),
      (readings) =>
        readings.map((t, i) => ({
          Index: i,
          Exclude: t.Exclude ?? false,
          CanExclude: t.CanExclude ?? false,
          CannotSetTemperature: t.CannotSetTemperature && !t.Temperature, // allow temperature to be set if it already has one, so that we can see any errors on it, and so the user can clear it
        }))
    ),
    deepEquals
  );

  const canGeneratePrototypeReadings = useSelector(
    combine(
      ['HydrometerPortion.Measurements.DateTimeTestStartedLocal', 'HydrometerPortion.Measurements.TimeSequence.Id'].map(
        (n) => pipe(createWorksheetSelector!(n), hasValue)
      ),
      (hasStartTime: boolean, hasTimeSequenceId: boolean) => hasStartTime && hasTimeSequenceId
    )
  );

  const anyReadingsExist = useSelector(
    pipe(createWorksheetSelector!('HydrometerPortion.Measurements.Readings'), hasNonEmptyValue)
  );

  const confirmDeleteReferenceReading = useSelector(
    pipe(createWorksheetSelector!('HydrometerPortion.Measurements.ReferenceReadings'), (readings: Array<any>) =>
      (readings ?? []).map((v) => hasNonEmptyValue(v.RawReading) || hasNonEmptyValue(v.Temperature))
    ),
    deepEquals
  );

  // disable all Exclude checkboxes that exist before (by index) the last checked box
  const disableExclusion = (i: number) =>
    i < Math.max(...readingRecordProps.map((r, i) => (readingRecordProps[i].Exclude ? i : 0)));

  // NOTE TO DEVELOPERS:
  // The mess below allows us to automatically check the box which shows all values, if
  // any of those values have messages on them. It provides a decent experience for the user,
  // but frankly this code is only prototype quality, so please do not propagate it to other
  // worksheets unless we can do a more generic implementation of it.
  const intermediateValueFields = ['TrueReading', 'EffectiveDepth', 'ModifiedReading'];
  const alwaysShowIntermediateValues = useSelector(
    pipe(createWorksheetSelector!('HydrometerPortion.Measurements.Readings'), (r) =>
      r.some((s: any) => hasNonEmptyValue(getMessagesForMultiplePaths(s, intermediateValueFields)))
    )
  );

  const [showIntermediateValues, setShowIntermediateValues] = useState(alwaysShowIntermediateValues);
  useEffect(() => {
    if (!showIntermediateValues && alwaysShowIntermediateValues) {
      setShowIntermediateValues(alwaysShowIntermediateValues);
    }
  }, [showIntermediateValues, alwaysShowIntermediateValues]);

  return (
    <>
      <PageSection heading="Hydrometer Test Measurements">
        <NamePrefixModifier appendNamePrefix="HydrometerPortion.Measurements">
          {/* TODO: put in a separate component */}
          <ListFieldSection heading="Equipment">
            <Field
              input={<WorksheetEquipmentDropdown />}
              equipmentType="sedimentation-cylinder"
              name="Equipment.SedimentationCylinder"
            />
            <FieldSet>
              <Field
                input={<WorksheetEquipmentDropdown />}
                equipmentType="hydrometer"
                importedCalibration={{
                  Mismatch: hydrometerCalibrationMismatch,
                  MeniscusCorrection: hydrometerMeniscusCorrection,
                  Intercept: hydrometerIntercept,
                  Slope: hydrometerSlope,
                }}
                name="Equipment.Hydrometer"
                importModel={{ id: hydrometerId }}
              />
              <Field
                input={<TextOutput />}
                name="HydrometerCalibrationMeniscusCorrection"
                visible={hasNonEmptyValue(hydrometerMeniscusCorrection)}
              />
              <Field
                input={<TextOutput />}
                name="HydrometerCalibrationIntercept"
                visible={hasNonEmptyValue(hydrometerIntercept)}
              />
              <Field
                input={<TextOutput />}
                name="HydrometerCalibrationSlope"
                visible={hasNonEmptyValue(hydrometerSlope)}
              />
            </FieldSet>
          </ListFieldSection>

          {/* TODO: put in a separate component */}
          <WorksheetGridFieldSection name="ReferenceReadings" heading="Reference Readings">
            <WorksheetRecordGrid
              name="ReferenceReadings"
              orientation="horizontal"
              recordProps={{
                enforceAtLeastOneRecord: true,
                heading: 'number',
                headingLabel: 'Reference Solution Reading ID',
                removeConfirm: {
                  //For a WorksheetRecordGrid, the `name` of the element passed into the confirm
                  //function is the index of the item being removed.
                  confirm: ({ name: i }) => confirmDeleteReferenceReading[i],
                  confirmTitle: 'Delete Reference Reading',
                  confirmText: 'Are you sure you want to delete the reference reading? Data cannot be recovered.',
                  confirmButtonText: 'Delete',
                },
              }}
              fieldSet={[
                {
                  name: 'RawReading',
                  component: <Field input={<NumericInput />} />,
                },
                {
                  name: 'Temperature',
                  component: <Field input={<NumericInput />} />,
                },
              ]}
            ></WorksheetRecordGrid>
          </WorksheetGridFieldSection>

          <SectionGroup heading="Measurements">
            {/* TODO: put in a separate component */}
            <ListFieldSection>
              <Field
                input={<RadioGroup />}
                name="ViscosityOfWaterDetermination"
                label="Viscosity of Water Determined By"
                options={[
                  {
                    label: 'Interpolation of values in standard',
                    value: ViscosityOfWaterDetermination.Interpolation,
                  },
                  {
                    label: 'Approximation calculation in standard',
                    value: ViscosityOfWaterDetermination.Approximation,
                  },
                ]}
              />
              {/* Android does not support date/time input to nearest second so we need to split it into separate fields. */}
              <Field
                input={<DateInput />}
                dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
                name="DateTimeTestStartedLocal"
                label="Test Start Date"
              />
              <Field
                input={<TimeInput />}
                dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
                name="DateTimeTestStartedLocal"
                label="Test Start Time"
              />
              <Field
                input={<QestlabListDropdown />}
                name="TimeSequence"
                disabled={busyCreatingReadings || anyReadingsExist}
                listType={ListType.TimeSequence}
                maxLength={50}
              />
            </ListFieldSection>
            <div>
              <Button
                disabled={busyCreatingReadings || anyReadingsExist || !canGeneratePrototypeReadings}
                onClick={() => dispatch(hydActions.createReadingsForTimeSequence())}
              >
                Populate Times
              </Button>
            </div>
            <Checkbox
              label="Show intermediate values in hydrometer table"
              value={showIntermediateValues}
              onChange={(e) => setShowIntermediateValues(e.target.checked)}
              disabled={alwaysShowIntermediateValues}
            />

            {/* TODO: put in a separate component */}
            {busyCreatingReadings ? (
              <div style={{ position: 'relative', height: '80px' }}>
                <Spinner />
              </div>
            ) : (
              <WorksheetGridFieldSection name="Readings">
                <WorksheetRecordGrid
                  name="Readings"
                  orientation="vertical"
                  recordProps={{}}
                  fieldSet={[
                    /* Android does not support date/time input to nearest second so we need to split it into separate fields. */
                    {
                      name: 'DateTimeReadingLocal',
                      component: (
                        <Field input={<DateInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />
                      ),
                      label: 'Date',
                    },
                    {
                      name: 'DateTimeReadingLocal',
                      component: (
                        <Field input={<TimeInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />
                      ),
                      label: 'Time',
                    },
                    { name: 'ElapsedTimeInMinutes', component: <Field input={<TextOutput />} /> },
                    {
                      name: 'RawReading',
                      component: <Field input={<NumericInput />} />,
                    },
                    {
                      name: 'Temperature',
                      component: (i) =>
                        readingRecordProps[i].CannotSetTemperature ? <></> : <Field input={<NumericInput />} />,
                    },
                    {
                      name: 'TrueReading',
                      component: <Field input={<TextOutput />} />,
                      hidden: !showIntermediateValues,
                    },
                    {
                      name: 'EffectiveDepth',
                      component: <Field input={<TextOutput />} />,
                      hidden: !showIntermediateValues,
                    },
                    {
                      name: 'ModifiedReading',
                      component: <Field input={<TextOutput />} />,
                      hidden: !showIntermediateValues,
                    },
                    {
                      name: 'ParticleDiameter',
                      component: <Field input={<TextOutput />} />,
                    },
                    {
                      name: 'MassSmallerPercentUnscaled',
                      component: <Field input={<TextOutput />} />,
                    },
                    {
                      name: 'MassSmallerPercentScaled',
                      component: <Field input={<TextOutput />} />,
                    },
                    /* The Exclude checkbox is only available when we have an imported sieve analysis because
                     its purpose is to prevent inconsistencies between the early hydrometer readings and the
                     tail-end of the sieve analysis curve - for example, the first few hydrometer readings
                     may calculate particle sizes larger than the finest sieves in the sieve analysis.
                     It is therefore not relevant in a stand-alone hydrometer test.
                     */
                    ...(hasImportedSieveAnalysis
                      ? [
                          {
                            name: 'Exclude',
                            component: (i: number) =>
                              readingRecordProps[i].CanExclude ? (
                                <Field input={<Checkbox />} disabled={disableExclusion(i)} />
                              ) : (
                                <></>
                              ),
                          },
                        ]
                      : []),
                  ]}
                ></WorksheetRecordGrid>
              </WorksheetGridFieldSection>
            )}
          </SectionGroup>
        </NamePrefixModifier>
      </PageSection>
    </>
  );
};
HydrometerMeasurementsPageSection = withInjectedProps(HydrometerMeasurementsPageSection);

export { HydrometerMeasurementsPageSection as HydrometerPortionMeasurementsSection };
export default HydrometerMeasurementsPageSection;
